import React, { useState, useRef, useEffect } from "react";
import "./singleTimeRange.css";

const pad = (n) => String(n).padStart(2, "0");

const todayDate = new Date();
const currentYear = todayDate.getFullYear();
const currentMonth = todayDate.getMonth();

const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Main SingleDatePicker Component (Date Only) ─────────────────────────
const SingleDatePicker = ({ date, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [cal, setCal] = useState({ year: currentYear, month: currentMonth });
  const [tempDate, setTempDate] = useState(null);

  const wrapRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    if (date && typeof date === 'string') {
      // Date is expected as "YYYY-MM-DD"
      setTempDate(date);
      const [y, m] = date.split("-").map(Number);
      if (y && m) setCal({ year: y, month: m - 1 });
    }
    setOpen(true);
  };

  // YYYY-MM-DD ko DD/MM/YYYY me badalne ka helper
  const toDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const handleManualInputChange = (val) => {
    // Regex updated for DD/MM/YYYY
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = val.match(regex);

    if (match) {
      const [_, d, m, y] = match; // Extract DD, MM, YYYY
      setTempDate(`${y}-${m}-${d}`);
      setCal({ year: Number(y), month: Number(m) - 1 });
    }
  };

  const prevMonth = () => setCal((prev) => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: prev.month - 1 });
  const nextMonth = () => setCal((prev) => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: prev.month + 1 });
  const prevYear = () => setCal((prev) => ({ ...prev, year: prev.year - 1 }));
  const nextYear = () => setCal((prev) => ({ ...prev, year: prev.year + 1 }));

  const handleDayClick = (ds) => {
    setTempDate(ds);
  };

  const handleOk = () => {
    if (tempDate) {
      onChange(tempDate); // Return just "YYYY-MM-DD"
      setOpen(false);
    }
  };

  const renderCalendar = (year, month) => {
    const cells = [];
    const first = firstDayOfMonth(year, month);
    const days = daysInMonth(year, month);
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);

    return (
      <div className="drp-calendar" style={{ minWidth: "250px" }}>
        <div className="drp-weekdays">
          {WEEKDAYS.map(w => <span key={w}>{w}</span>)}
        </div>
        <div className="drp-days">
          {cells.map((d, i) => {
            if (!d) return <span key={`e-${i}`} className="drp-day disabled"></span>;
            const ds = `${year}-${pad(month + 1)}-${pad(d)}`;
            const isSelected = ds === tempDate;
            const isToday = ds === `${currentYear}-${pad(currentMonth + 1)}-${pad(todayDate.getDate())}`;

            let classes = "drp-day";
            if (isToday) classes += " today";
            if (isSelected) classes += " selected";

            return (
              <span key={d} className={classes} onClick={() => handleDayClick(ds)}>
                {d}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const formatDisplay = (dtStr) => {
    if (!dtStr || typeof dtStr !== 'string') return "";
    // Helper function use karke format change kiya
    return toDDMMYYYY(dtStr);
  };

  return (
    <div className="drp-wrapper" ref={wrapRef}>
      <div className={`drp-input ${disabled ? "disabled" : ""} ${open ? "focused" : ""}`} onClick={handleOpen}>
        <span style={{ fontSize: '15px', color: date ? '#9fa5ac' : '#9fa5ac' }}>
          {date ? formatDisplay(date) : "Select date"}
        </span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>

      {open && (
        <div className="drp-dropdown" style={{ minWidth: '300px' }}>
          <div className="drp-header" style={{ justifyContent: 'center' }}>
            <input
              key={tempDate}
              className="drp-header-input"
              style={{ width: '120px', marginLeft: 0 }}
              defaultValue={tempDate ? toDDMMYYYY(tempDate) : ""}
              placeholder="DD/MM/YYYY"
              onBlur={(e) => handleManualInputChange(e.target.value)}
            />
          </div>

          <div className="drp-nav">
            <div className="drp-nav-group"><button onClick={prevYear}>&laquo;</button><button onClick={prevMonth}>&lsaquo;</button></div>
            <div className="drp-month-title">{MONTHS[cal.month]} {cal.year}</div>
            <div className="drp-nav-group"><button onClick={nextMonth}>&rsaquo;</button><button onClick={nextYear}>&raquo;</button></div>
          </div>

          <div className="drp-calendars-container" style={{ justifyContent: 'center' }}>
            {renderCalendar(cal.year, cal.month)}
          </div>

          <div className="drp-footer" style={{ justifyContent: 'flex-end' }}>
            <button className="drp-ok-btn" onClick={handleOk} disabled={!tempDate}>Ok</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleDatePicker;